import { useTranslation } from "react-i18next";

import DataTable from "components/elements/DataTable";
import { ColumnType } from "components/elements/DataTable/DataTable";
import View from "components/elements/View";
import { useGetMonitoringDataQuery } from "store/monitoring";
import { QuotaTable, StateTableData } from "types/monitoring.types";
import { Divider } from "antd";

function Monitoring(): JSX.Element {
  const { t } = useTranslation();

  const { data, isLoading, isFetching } = useGetMonitoringDataQuery();

  const generateQuotaTableData = (quotaTableData?: QuotaTable) => {
    if (!quotaTableData) {
      return [];
    }
    const sortedKeys = Object.keys(quotaTableData).sort((a, b) => {
      const order = { soll: 1, istBrutto: 2, istNetto: 3 };
      return order[a as keyof typeof order] - order[b as keyof typeof order];
    });

    return sortedKeys.reduce(
      (
        acc: {
          id: string;
          total: string;
          german: string;
          french: string;
          italian: string;
        }[],
        curr: string
      ) => {
        const currData = quotaTableData[curr as keyof QuotaTable];
        acc.push({
          id: t(`monitoring.table.values.${curr}`) as string,
          total: `${currData.totalNumber} (${currData.totalPercentage}%)`,
          german: `${currData.germanNumber}`,
          french: `${currData.frenchNumber}`,
          italian: `${currData.italianNumber}`,
        });
        return acc;
      },
      []
    );
  };

  const generateStateTableData = (stateEntries?: StateTableData[]) => {
    if (!stateEntries) {
      return [];
    }
    return stateEntries.map((item) => ({
      id: item.rekStatus,
      quota: `${item.rekStatus ? `${item.rekStatus} - ` : ""}${t(
        `masterdata.enums.RekStatusEnum.${item.rekStatus}`
      )}`,
      total: `${item.totalNumber} (${item.totalPercentage}%)`,
      german: `${item.germanNumber} (${item.germanPercentage}%)`,
      french: `${item.frenchNumber} (${item.frenchPercentage}%)`,
      italian: `${item.italianNumber} (${item.italianPercentage}%)`,
    }));
  };

  return (
    <View title={t("monitoring.title")}>
      <DataTable
        disablePagination
        title={null}
        isLoading={isLoading || isFetching}
        headers={[
          {
            title: t("monitoring.table.labels.quota"),
            key: "id",
            type: ColumnType.Text,
            withSearchFilter: true,
            width: 120,
          },
          {
            title: t("monitoring.table.labels.totalNumber"),
            key: "total",
            type: ColumnType.Text,
            withSearchFilter: true,
            width: 120,
          },
          {
            title: t("monitoring.table.labels.germanNumber"),
            key: "german",
            type: ColumnType.Text,
            withSearchFilter: true,
            width: 120,
          },
          {
            title: t("monitoring.table.labels.frenchNumber"),
            key: "french",
            type: ColumnType.Text,
            withSearchFilter: true,
            width: 120,
          },
          {
            title: t("monitoring.table.labels.italianNumber"),
            key: "italian",
            type: ColumnType.Text,
            withSearchFilter: true,
            width: 120,
          },
        ]}
        actions={[]}
        data={generateQuotaTableData(data?.quotaTable)}
        stickyHeader
      />
      <Divider />
      <DataTable
        disablePagination
        isLoading={isLoading || isFetching}
        headers={[
          {
            title: t("monitoring.table.values.totalStateTable"),
            key: "quota",
            type: ColumnType.Text,
            withSearchFilter: true,
            width: 120,
          },
          {
            title: `${data?.stateTable.totalEntry.totalNumber} (${data?.stateTable.totalEntry.totalPercentage}%)`,
            key: "total",
            type: ColumnType.Text,
            withSearchFilter: true,
            width: 120,
          },
          {
            title: `${data?.stateTable.totalEntry.germanNumber} (${data?.stateTable.totalEntry.germanPercentage}%)`,
            key: "german",
            type: ColumnType.Text,
            withSearchFilter: true,
            width: 120,
          },
          {
            title: `${data?.stateTable.totalEntry.frenchNumber} (${data?.stateTable.totalEntry.frenchPercentage}%)`,
            key: "french",
            type: ColumnType.Text,
            withSearchFilter: true,
            width: 120,
          },
          {
            title: `${data?.stateTable.totalEntry.italianNumber} (${data?.stateTable.totalEntry.italianPercentage}%)`,
            key: "italian",
            type: ColumnType.Text,
            withSearchFilter: true,
            width: 120,
          },
        ]}
        actions={[]}
        data={generateStateTableData(data?.stateTable.stateEntries)}
        stickyHeader
      />
    </View>
  );
}

export default Monitoring;
